import axios from 'axios';
import { LLMRequestDTO, ServerResponse, FeatureList, ApiError, FeatureRefinementRequest, FeatureRefinementResponse } from '../types/api';
import { config } from '../config';
import { APP_CONSTANTS, ERROR_MESSAGES } from '../utils/constants';

// Базовый URL для REST API
const API_BASE_URL = config.api.baseUrl;

// Создаем экземпляр axios с настройками для REST API
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: APP_CONSTANTS.API_TIMEOUT,
});

// Интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Сервер ответил с ошибкой
      console.error('API Error:', error.response.data);
    } else if (error.request) {
      // Запрос был отправлен, но ответ не получен
      console.error('Network Error:', error.request);
    } else {
      // Ошибка при настройке запроса
      console.error('Request Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export const llmService = {
  /**
   * Отправляет REST запрос к LLM для генерации фич-листа
   */
  async generateFeatures(request: LLMRequestDTO): Promise<FeatureList> {
    try {
      console.log('Отправка REST запроса:', request);
      
      const response = await api.post('/llm/generate', request);
      
      console.log('Получен REST ответ:', response.data);
      
      const serverResponse: ServerResponse = response.data;
      
      // Проверяем успешность операции
      if (!serverResponse.success) {
        throw new Error(serverResponse.message || 'Операция не выполнена');
      }
      
      // Проверяем наличие фич-листа
      if (!serverResponse.featureList || !serverResponse.featureList.groups) {
        throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
      }
      
      return serverResponse.featureList;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Сервер вернул ошибку
          const errorData = error.response.data as ApiError;
          throw new Error(errorData.error || `HTTP ${error.response.status}: ${error.response.statusText}`);
        } else if (error.request) {
          // Нет ответа от сервера
          throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
        } else {
          // Ошибка настройки запроса
          throw new Error(`${ERROR_MESSAGES.REQUEST_ERROR}: ${error.message}`);
        }
      }
      throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
    }
  },

  /**
   * Отправляет REST запрос к LLM для доуточнения фичи
   */
  async refineFeature(request: FeatureRefinementRequest): Promise<FeatureRefinementResponse> {
    try {
      console.log('Отправка запроса на доуточнение фичи:', request);
      
      const response = await api.post('/llm/feature-refinement', request);
      
      console.log('Получен ответ на доуточнение фичи:', response.data);
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Сервер вернул ошибку
          const errorData = error.response.data as ApiError;
          throw new Error(errorData.error || `HTTP ${error.response.status}: ${error.response.statusText}`);
        } else if (error.request) {
          // Нет ответа от сервера
          throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
        } else {
          // Ошибка настройки запроса
          throw new Error(`${ERROR_MESSAGES.REQUEST_ERROR}: ${error.message}`);
        }
      }
      throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
    }
  },

  /**
   * Проверяет состояние сервера через REST API
   */
  async checkHealth(): Promise<{ status: string; message: string }> {
    try {
      const response = await api.get('/llm/health');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(`${ERROR_MESSAGES.SERVER_ERROR}: ${error.response.status}`);
      }
      throw new Error(ERROR_MESSAGES.SERVER_ERROR);
    }
  }
};

export default api; 